/**
 * UserInsightsTrends: the User Analytics 30/60/90-day trend, in Carbon.
 *
 * A layer-01 tile: the section heading and one helper line, a content
 * switcher for the period, the Carbon TrendChart (key above), then a
 * condensed grid of four summary tiles (current value and the change against
 * the prior period).
 *
 * The chart reads two axes: the percent series (MFA adoption, phish-resistant
 * share) on the left and the counts (license waste, total users) on the right,
 * the right-axis lines dashed by the primitive so the two scales never read as
 * one. The Identity pillar's mark draws MFA adoption, its headline metric; the
 * other three series are neutral or muted inks. Deltas are neutral tags with a
 * word (Up / Down), never a green or red figure (the Neutral Bad State Rule).
 */

import { useMemo } from 'react'
import { format } from 'date-fns'
import type { UserInsightsTrendsResult, MetricTrendResult } from '@/types'
import { BigNumber } from '@/components/carbon/BigNumber'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { ContentSwitcher } from '@/components/carbon/ContentSwitcher'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { TrendChart, type TrendSeries } from '@/components/carbon/TrendChart'

interface UserInsightsTrendsProps {
  trends: UserInsightsTrendsResult | null
  isLoading: boolean
  days: number
  onDaysChange: (days: number) => void
}

const DAY_OPTIONS = [30, 60, 90] as const

/** "Up 2.1 pp", "Down 3", "No change": the word carries the direction, never a hue. */
function changeText(metric: MetricTrendResult, isPercentage: boolean): string {
  if (metric.direction === 'flat' || metric.changeAmount === null) return 'No change'
  const amount = isPercentage
    ? `${Math.abs(metric.changeAmount).toFixed(1)} pp`
    : Math.abs(metric.changeAmount).toLocaleString()
  return `${metric.direction === 'up' ? 'Up' : 'Down'} ${amount}`
}

/** One summary tile: the label, the current value, and a neutral change tag. */
function TrendSummaryTile({
  label,
  metric,
  isPercentage,
}: {
  label: string
  metric: MetricTrendResult
  isPercentage: boolean
}) {
  const formattedValue = isPercentage
    ? `${metric.currentValue.toFixed(1)}%`
    : metric.currentValue.toLocaleString()

  return (
    <Tile className="flex min-w-0 flex-col">
      <TileHeader title={label} level={3} />
      <BigNumber value={formattedValue} size="figure" className="mt-4" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Tag className="tabular-nums">{changeText(metric, isPercentage)}</Tag>
        <span className="text-helper text-text-helper">vs prior period</span>
      </div>
    </Tile>
  )
}

type SeriesKey = 'mfaAdoption' | 'phishResistantMfa' | 'licenseWaste' | 'totalUsers'

/** Counts read the right axis: "1.2k" past a thousand. */
const formatCount = (value: number) => (value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value))

const percent = (value: number) => `${value}%`

export default function UserInsightsTrends({
  trends,
  isLoading,
  days,
  onDaysChange,
}: UserInsightsTrendsProps) {
  // Transform history data for the chart
  const chartData = useMemo(() => {
    if (!trends?.history || !Array.isArray(trends.history)) return []
    return trends.history.map((point) => ({
      date: format(new Date(point.date), 'MMM d'),
      mfaAdoption: point.mfaMemberAdoptionPercentage,
      phishResistantMfa: point.phishResistantMfaPercentage,
      licenseWaste: point.totalLicenseWaste,
      totalUsers: point.totalUsers,
    }))
  }, [trends?.history])

  const series = useMemo<TrendSeries[]>(() => {
    const line = (key: SeriesKey, label: string, category: TrendSeries['category'], axis: 'left' | 'right') => ({
      id: key,
      label,
      category,
      axis,
      points: chartData.map(row => ({ x: row.date, y: row[key] })),
    })
    return [
      line('mfaAdoption', 'MFA adoption %', 'identity', 'left'),
      line('phishResistantMfa', 'Phish-resistant MFA %', 'neutral', 'left'),
      line('licenseWaste', 'License waste', 'neutral', 'right'),
      line('totalUsers', 'Total users', 'muted', 'right'),
    ]
  }, [chartData])

  const hasEnoughData = chartData.length >= 2
  const headingId = 'user-insights-trend-heading'

  if (isLoading) {
    return (
      <Tile as="section" aria-busy="true" aria-label="Adoption trend" className="min-w-0">
        <Skeleton lines={2} width="40%" />
        <div aria-hidden="true" className="mt-6 h-[260px] animate-pulse bg-layer-hover" />
        <Skeleton lines={1} label="Loading the adoption trend" className="mt-6" />
      </Tile>
    )
  }

  return (
    <section aria-labelledby={headingId} className="min-w-0">
      <Tile className="min-w-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 id={headingId} className="m-0 text-heading font-normal text-text-primary">
              Adoption trend
            </h2>
            <p className="m-0 mt-1 text-helper text-text-helper">
              MFA adoption, phish-resistant share, license waste and headcount per day over the last {days} days.
            </p>
          </div>
          <ContentSwitcher
            aria-label="Trend period"
            items={DAY_OPTIONS.map(option => ({ id: String(option), label: `${option} days` }))}
            value={String(days)}
            onChange={id => onDaysChange(Number(id))}
          />
        </div>

        {!hasEnoughData ? (
          <div className="mt-6 border-t border-line-subtle py-12">
            <p className="m-0 text-heading font-normal text-text-primary">Not enough history yet.</p>
            <p className="m-0 mt-2 max-w-[48ch] text-body text-text-secondary">
              A trend needs at least two scans on file. The lines appear here after the next scan.
            </p>
          </div>
        ) : (
          <>
            <TrendChart
              series={series}
              yDomain={[0, 100]}
              yFormat={percent}
              rightAxis={{ format: formatCount }}
              xDomain={chartData.map(row => row.date)}
              className="mt-6"
            />
          </>
        )}
      </Tile>

      {trends?.trendSummary && (
        <CondensedGrid
          aria-label="Change against the prior period"
          columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }}
          className="mt-4"
        >
          <TrendSummaryTile
            label="MFA adoption"
            metric={trends.trendSummary.mfaAdoption}
            isPercentage={true}
          />
          <TrendSummaryTile
            label="Phish-resistant"
            metric={trends.trendSummary.phishResistantMfa}
            isPercentage={true}
          />
          <TrendSummaryTile
            label="License waste"
            metric={trends.trendSummary.licenseWaste}
            isPercentage={false}
          />
          <TrendSummaryTile
            label="Total users"
            metric={trends.trendSummary.totalUsers}
            isPercentage={false}
          />
        </CondensedGrid>
      )}
    </section>
  )
}